import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { Box, Heading, Text } from "@chakra-ui/react";
import Button from "../../components/Button";
import Input from "../../components/Input";
import Modal from "../../components/Modal";
import useModal from "../../components/Modal/hooks/useModal";
import Pagination from "../../components/Pagination";
import useSyncPagination, { DataType } from "../../components/Pagination/hooks/useSyncPagination";
import Table from "../../components/Table";
import { TableComposite } from "../../components/Table/types";
import { baseURL } from "../../api/api";
import { Reconciliation } from "../../api/types";
import useGetOrganization from "../../api/useGetOrganization";
import useGetReconciliations from "../../api/useGetReconciliations";
import { EntityId } from "../../hooks/useEntitySelection";
import useFilter from "../../hooks/useFilter";
import useWebSocketMessage from "../../hooks/useWebSocketMessage";
import collectionCountLabel from "../../utils/collectionCountLabel";
import notification from "../../utils/notification";
import { typedSxMap } from "../../utils/typedSxMap";
import { tableRowNavigation } from "../../utils/navigation";
import ActionModal from "./components/actionModal";
import ReportReconciliationForm from "./components/reportReconciliationForm";
import { reconciliationsTable } from "./utils/reconciliationsTable";
import style from "../forms/style/Form.module.scss";
import { PiMagnifyingGlassBold } from "react-icons/pi";

const styles = typedSxMap({
  importers: {
    flexGrow: 1,
    height: "100%",
  },
  container: {
    "&>:last-child": {
      marginTop: "var(--m-xs)",
      padding: 0,
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "var(--m-xs)",
    marginBottom: "var(--m-xs)",
  },
  title: {
    "& h1": {
      marginBottom: 0,
    },
  },
  dragContainer: {
    position: "relative",
  },
  dropZone: {
    background: "color-mix(in srgb, var(--color-primary) 20%, transparent)",
    position: "absolute",
    height: 0,
    width: "100%",
    top: 0,
    left: 0,
    right: 0,
    border: "2px dashed var(--color-text)",
    borderRadius: "8px",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    opacity: 0,
    zIndex: 10,
    // transition: "all 0.2s ease",
  },
  dropzonesContainer: {
    display: "flex",
    gap: "var(--m)",
    marginBottom: "var(--m)",
    width: "100%",
    maxWidth: "700px",
    justifyContent: "space-between",
    paddingBottom: "var(--m-s)",
    margin: "0",
  },
  dropzoneWrapper: {
    width: "calc(50% - var(--m)/2)",
    maxWidth: "380px",
    display: "flex",
    flexDirection: "column",
    gap: "var(--m-xxxxs)",
  },
  dropzoneLabel: {
    fontWeight: 500,
    fontSize: "16px",
    color: "var(--color-text)",
    marginBottom: "var(--m-xxs)",
  },
  uploadBox: {
    position: "relative",
    border: "1px solid var(--color-border)",
    borderRadius: "8px",
    padding: "var(--m)",
    backgroundColor: "var(--color-background-modal)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "120px",
    height: "120px",
    maxWidth: "380px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "var(--color-primary)",
      backgroundColor: "color-mix(in srgb, var(--color-background-modal) 95%, var(--color-primary))",
    },
  },
  fileName: {
    marginTop: "var(--m-xs)",
    color: "var(--color-text-soft)",
    fontSize: "14px",
    wordBreak: "break-all",
    textAlign: "center",
    minHeight: "2.4em",
  },
  uploadText: {
    color: "var(--color-text)",
    fontSize: "14px",
    textAlign: "center",
    paddingTop: "var(--m-xs)",
    marginBottom: "var(--m-xxs) !important",
  },
  uploadedFileName: {
    color: "var(--color-primary)",
    fontSize: "14px",
    fontWeight: "500",
    marginTop: "var(--m-xs)",
    wordBreak: "normal",
    textAlign: "center",
    display: "-webkit-box",
    "-webkit-line-clamp": "2",
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
    maxWidth: "340px",
    lineHeight: "1.2",
    minHeight: "2.4em",
  },
  footer: {
    marginTop: "auto",
    display: "flex",
    justifyContent: "flex-end",
    padding: "var(--m) 0",
  },
  uploadBoxWrapper: {
    position: "relative",
    height: "100%",
  },
  actions: {
    display: "flex",
    gap: "var(--m-xs)",
    "& .searchInput": {
      marginBottom: 0,
    },
  },
  tableWrapper: {
    display: "flex",
    overflowY: "auto",
    padding: "1px",
    marginRight: "-20px",
    paddingRight: "21px",
    height: "auto",
    maxHeight: "calc(100vh - 126px)",
  },
  tableWrapperWithPagination: {
    maxHeight: "calc(100vh - 180px)",
  },
});

const CLICKABLE_STATUSES = ["complete", "no_match", "importing", "review", "fail", "reported", "rerun"];

export default function Reconciliations() {
  const { data: organization } = useGetOrganization();
  const workspaceId = organization?.workspaces?.[0]?.id || "-";
  const { isLoading, data: reconciliations } = useGetReconciliations(workspaceId);
  const [purchaseOrderFile, setPurchaseOrderFile] = useState<File | null>(null);
  const [packingListFile, setPackingListFile] = useState<File | null>(null);
  const queryClient = useQueryClient();
  const message = useWebSocketMessage();
  const navigate = useNavigate();

  const poFileInputRef = useRef<HTMLInputElement>(null);
  const plFileInputRef = useRef<HTMLInputElement>(null);
  const poContentRef = useRef<HTMLDivElement>(null);
  const plContentRef = useRef<HTMLDivElement>(null);
  const poDropZoneRef = useRef<HTMLDivElement>(null);
  const plDropZoneRef = useRef<HTMLDivElement>(null);

  const [actionModal, setActionModal] = useState<{
    selectedReconciliation: Reconciliation | null;
    action: "report" | null;
    title: string | null;
  }>({
    action: null,
    selectedReconciliation: null,
    title: null,
  });

  const { dataFiltered, setFilter: setFilterValue } = useFilter<Reconciliation[]>(["name"], reconciliations || []);
  const itemsPerPage = 25;
  const { dataPage, page, paginate, totalItems } = useSyncPagination(dataFiltered as unknown as DataType, itemsPerPage);

  const tableData = reconciliationsTable(dataPage as unknown as Reconciliation[], (entityId: EntityId, action) =>
    setActionModal({
      selectedReconciliation: reconciliations?.find((r) => r.id === (entityId as string)) || null,
      action: action === "report" ? action : null,
      title: action === "report" ? "Report an Issue" : "",
    })
  );

  const modalContent = useMemo(() => {
    switch (actionModal.action) {
      case "report":
        return (
          <Box>
            <ReportReconciliationForm
              action={actionModal.action}
              reconciliation={actionModal.selectedReconciliation}
              workspaceId={workspaceId}
              onSuccess={function (data: any): void {
                setActionModal({ action: null, selectedReconciliation: null, title: null });
              }}></ReportReconciliationForm>
          </Box>
        );
      default:
        return <></>;
    }
  }, [actionModal.action]);

  const modal = useModal();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reconciliationName, setReconciliationName] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (message?.data === "refresh") {
        await queryClient.invalidateQueries(["reconciliations", workspaceId]);
      }
    };
    fetchData();
  }, [message, queryClient, workspaceId]);

  const handleClickedRow = (row: any) => {
    // Use the tableRowNavigation utility with a conditional check
    tableRowNavigation(
      row,
      navigate,
      "/reconciliation/${id}",
      (data) => {
        // Only navigate if the reconciliation status is clickable
        const status = data.Status as TableComposite;
        return status?.raw && CLICKABLE_STATUSES.includes(status?.raw as string);
      }
    );
  };

  const handleDragOver = (type: "po" | "pl") => {
    const dropZoneRef = type === "po" ? poDropZoneRef : plDropZoneRef;
    const contentRef = type === "po" ? poContentRef : plContentRef;

    if (dropZoneRef.current && contentRef.current) {
      dropZoneRef.current.style.height = "100%";
      dropZoneRef.current.style.opacity = "1";
      contentRef.current.style.filter = "blur(5px)";
    }
  };

  const hideDropZone = (type: "po" | "pl", event: React.DragEvent) => {
    event.preventDefault();
    const dropZoneRef = type === "po" ? poDropZoneRef : plDropZoneRef;
    const contentRef = type === "po" ? poContentRef : plContentRef;

    if (dropZoneRef.current && contentRef.current) {
      dropZoneRef.current.style.height = "0";
      dropZoneRef.current.style.opacity = "0";
      contentRef.current.style.filter = "none";
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, type: "po" | "pl") => {
    const file = event.target.files?.[0];
    if (file) {
      if (type === "po") {
        setPurchaseOrderFile(file);
      } else {
        setPackingListFile(file);
      }
    }
  };

  const handleDrop = (type: "po" | "pl", event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      if (type === "po") {
        setPurchaseOrderFile(file);
      } else {
        setPackingListFile(file);
      }
    }
    hideDropZone(type, event);
  };

  const handleSubmit = async () => {
    if (!purchaseOrderFile || !packingListFile) return;
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("file1", purchaseOrderFile, purchaseOrderFile.name);
    formData.append("file2", packingListFile, packingListFile.name);
    formData.append("name", reconciliationName || "");

    try {
      const response = await fetch(`${baseURL}reconciliations/${workspaceId}/upload`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      notification({
        title: "Success",
        message: "Upload successful",
        type: "success",
      });
      modal.setOpen(false);
      setPurchaseOrderFile(null);
      setPackingListFile(null);
      if (poFileInputRef.current) poFileInputRef.current.value = "";
      if (plFileInputRef.current) plFileInputRef.current.value = "";
      setReconciliationName("");
    } catch (error) {
      notification({
        title: "Error",
        message: error instanceof Error ? error.message : "Unknown error occurred",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={styles.importers}>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Box sx={styles.title}>
            <Heading as="h1">Reconciliations</Heading>
            <small>
              {!isLoading
                ? collectionCountLabel(
                    "No reconciliations",
                    "reconciliation",
                    "reconciliations",
                    reconciliations?.length || 0,
                    "No",
                    `reconciliation${dataFiltered.length === 1 ? "" : "s"} found from`,
                    dataFiltered.length
                  )
                : "Loading..."}
            </small>
          </Box>
          <Box sx={styles.actions}>
            <Input
              className="searchInput"
              icon={<PiMagnifyingGlassBold />}
              type="search"
              autoComplete="off"
              placeholder="Search"
              onChange={(e) => setFilterValue(e.target.value)}
            />
            <Button variants={["primary"]} onClick={() => modal.setOpen(true)}>
              Create
            </Button>
          </Box>
        </Box>

        <Box sx={styles.dragContainer}>
          <Box sx={{ ...styles.tableWrapper, ...(!!(totalItems && totalItems > itemsPerPage) ? styles.tableWrapperWithPagination : {}) }}>
            <Table
              onRowClick={handleClickedRow}
              data={tableData}
              emptyState={<Box>No reconciliations found</Box>}
              emptyMessageStyles={{ width: "100%" }}
              columnWidths={["60%", "25%", "15%"]}
              hideColumns={["id", "_drive_file_id"]}
              getRowStyles={(datum: any) => ({
                cursor: CLICKABLE_STATUSES.includes(datum.Status.raw) ? "pointer" : "auto",
              })}
            />
          </Box>
        </Box>

        {!!(totalItems && totalItems > itemsPerPage) && (
          <Pagination totalItems={totalItems} itemsPerPage={itemsPerPage} onPageChange={paginate} initialPage={page} />
        )}
      </Box>

      {modal.openDelayed && (
        <Modal {...modal} useCloseButton>
          <Box style={{ minWidth: "700px", margin: "0 auto" }}>
            <Box className={style.title}>
              <Heading as="h2" size="m">
                Create Reconciliation
              </Heading>
            </Box>
            <Box sx={styles.dropzonesContainer}>
              <Box sx={styles.dropzoneWrapper}>
                <Text sx={styles.dropzoneLabel}>Purchase Order</Text>
                <Box
                  sx={styles.uploadBoxWrapper}
                  onDragOver={(e) => {
                    e.preventDefault();
                    handleDragOver("po");
                  }}>
                  <input
                    type="file"
                    accept=".csv,.xlsx"
                    style={{ display: "none" }}
                    ref={poFileInputRef}
                    onChange={(e) => handleFileUpload(e, "po")}
                  />
                  <Box ref={poContentRef} sx={styles.uploadBox} onClick={() => poFileInputRef.current?.click()}>
                    <Text sx={styles.uploadText}>Click or drag file to upload</Text>
                    {purchaseOrderFile ? (
                      <Text sx={styles.uploadedFileName}>{purchaseOrderFile.name}</Text>
                    ) : (
                      <Text sx={styles.fileName}>Accepts .xlsx or .csv files</Text>
                    )}
                  </Box>
                  <Box
                    ref={poDropZoneRef}
                    sx={styles.dropZone}
                    onDragOver={(e) => e.preventDefault()}
                    onDragLeave={(e) => hideDropZone("po", e)}
                    onDrop={(e) => handleDrop("po", e)}>
                    <Text sx={styles.uploadText}>Drop file here</Text>
                  </Box>
                </Box>
              </Box>

              <Box sx={styles.dropzoneWrapper}>
                <Text sx={styles.dropzoneLabel}>Packing List</Text>
                <Box
                  sx={styles.uploadBoxWrapper}
                  onDragOver={(e) => {
                    e.preventDefault();
                    handleDragOver("pl");
                  }}>
                  <input
                    type="file"
                    accept=".csv,.xlsx"
                    style={{ display: "none" }}
                    ref={plFileInputRef}
                    onChange={(e) => handleFileUpload(e, "pl")}
                  />
                  <Box ref={plContentRef} sx={styles.uploadBox} onClick={() => plFileInputRef.current?.click()}>
                    <Text sx={styles.uploadText}>Click or drag file to upload</Text>
                    {packingListFile ? (
                      <Text sx={styles.uploadedFileName}>{packingListFile.name}</Text>
                    ) : (
                      <Text sx={styles.fileName}>Accepts .xlsx or .csv files</Text>
                    )}
                  </Box>
                  <Box
                    ref={plDropZoneRef}
                    sx={styles.dropZone}
                    onDragOver={(e) => e.preventDefault()}
                    onDragLeave={(e) => hideDropZone("pl", e)}
                    onDrop={(e) => handleDrop("pl", e)}>
                    <Text sx={styles.uploadText}>Drop file here</Text>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ marginBottom: "var(--m)" }}>
              <Input
                type="text"
                onChange={(e) => setReconciliationName(e.target.value)}
                value={reconciliationName}
                label="Name"
                autoComplete="off"
                disabled={isSubmitting}
              />
            </Box>
            <Box sx={styles.actions} style={{ justifyContent: "flex-end" }}>
              <Button variants={["primary", "noMargin"]} disabled={!purchaseOrderFile || !packingListFile || isSubmitting} onClick={handleSubmit}>
                {isSubmitting ? "Please Wait..." : "Submit"}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      <ActionModal
        title={actionModal.title}
        isOpen={!!actionModal.action}
        onClose={() => setActionModal({ action: null, selectedReconciliation: null, title: null })}
        workspaceId={workspaceId}
        reconciliation={actionModal.selectedReconciliation}>
        {modalContent}
      </ActionModal>
    </Box>
  );
}
